import React, { forwardRef } from 'react';
import styled from '@emotion/styled';
import { Clock, Monitor } from 'lucide-react';
import { media } from '../../styles/breakpoints';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const Section = styled.section`
  min-height: calc(var(--kuutio-vh, 100vh) - var(--kuutio-admin-header-height, 60px) - 5px);
  scroll-snap-align: start;
  scroll-margin-top: var(--kuutio-admin-header-height, 60px);
  display: flex;
  flex-direction: column;
  padding: 32px 40px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);

  ${media.sm} {
    min-height: 0;
    scroll-snap-align: none;
    padding: 24px 16px;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 24px;
  flex-shrink: 0;

  ${media.sm} {
    gap: 12px;
    margin-bottom: 16px;
  }
`;

const StepBadge = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 700;
  flex-shrink: 0;
  background: ${p =>
    p.$active ? '#4a9eff' :
    p.$past ? 'rgba(34, 197, 94, 0.2)' :
    'rgba(255,255,255,0.08)'};
  color: ${p =>
    p.$active ? '#fff' :
    p.$past ? '#22c55e' :
    '#888'};

  ${media.sm} {
    width: 36px;
    height: 36px;
    font-size: 14px;
    border-radius: 10px;
  }
`;

const HeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const StepName = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #fff;
  margin: 0 0 6px 0;
  letter-spacing: -0.3px;

  ${media.sm} {
    font-size: 18px;
  }
`;

const StepDescription = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  margin: 0;
  line-height: 1.5;
  max-width: 600px;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: #666;

  svg { opacity: 0.6; }
`;

const Content = styled.div`
  flex: 1;
  min-height: 0;
`;

const EmptyContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 32px;
  text-align: center;
  color: rgba(255, 255, 255, 0.25);
  gap: 12px;

  ${media.sm} {
    padding: 40px 16px;
  }
`;

const EmptyTitle = styled.div`
  font-size: 15px;
  font-weight: 500;
`;

const EmptySubtitle = styled.div`
  font-size: 13px;
  color: rgba(255, 255, 255, 0.18);
  max-width: 400px;
  line-height: 1.5;
`;

function formatDuration(minutes) {
  if (!minutes) return null;
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h} h ${m} min` : `${h} h`;
}

function getScreenLabel(screenConfig) {
  if (!screenConfig) return null;
  let config = screenConfig;
  if (typeof config === 'string') {
    try { config = JSON.parse(config); } catch { return null; }
  }
  const screens = config.screens || {};
  const primary = screens.primary?.view;
  const secondary = screens.secondary?.view;
  if (primary && secondary && primary !== secondary) return `${primary} / ${secondary}`;
  return primary || secondary || config.walls?.view || null;
}

/**
 * PhaseSection - Full-viewport scroll-snap section for one module of the
 * workshop chain. Renders header with step info + children (module content) or
 * empty state. Numbered by position in the chain.
 */
const PhaseSection = forwardRef(function PhaseSection({
  step,
  stepIndex,
  isActive,
  isPast,
  children,
}, ref) {
  const { t } = useTranslation();
  const duration = step.suggested_duration || step.suggestedDuration;
  const screenLabel = getScreenLabel(step.screen_config || step.screenConfig);

  return (
    <Section ref={ref} data-phase-index={stepIndex}>
      <SectionHeader>
        <StepBadge $active={isActive} $past={isPast}>
          {stepIndex + 1}
        </StepBadge>
        <HeaderInfo>
          <StepName>{step.name}</StepName>
          {step.description && (
            <StepDescription>{step.description}</StepDescription>
          )}
          <MetaRow>
            {duration && (
              <MetaItem>
                <Clock size={13} />
                {formatDuration(duration)}
              </MetaItem>
            )}
            {screenLabel && (
              <MetaItem>
                <Monitor size={13} />
                {screenLabel}
              </MetaItem>
            )}
          </MetaRow>
        </HeaderInfo>
      </SectionHeader>

      <Content>
        {children || (
          <EmptyContent>
            <EmptyTitle>{t('workshopOrchestrator.noSettingsTitle')}</EmptyTitle>
            <EmptySubtitle>
              {step.description || t('workshopOrchestrator.noSettingsBody')}
            </EmptySubtitle>
          </EmptyContent>
        )}
      </Content>
    </Section>
  );
});

export default PhaseSection;
